(() => {
  if (window.__saDashcamQuizLoaded) return;
  window.__saDashcamQuizLoaded = true;

  const SECTION_SELECTOR = '[data-sa-dashcam-quiz]';
  const isLargeVehicle = (vehicleType) => ['van_camper', 'truck_bus'].includes(vehicleType);
  const shouldSkipMountAndCamera = (answers) => isLargeVehicle(answers.vehicle_type) && answers.window_shape === 'recht';

  const QUIZ_STEPS = [
    {
      key: 'vehicle_type',
      shortLabel: 'Voertuig',
      summaryLabel: 'Voertuig',
      kicker: 'Vraag 1',
      question: 'Welk voertuig heb je?',
      helper: 'Zo bepalen we direct welke montage en dekking logisch is voor jouw situatie.',
      options: [
        { value: 'auto', label: 'Auto', description: 'Voor dagelijks gebruik en standaard voorruiten.' },
        { value: 'taxi', label: 'Taxi', description: 'Extra focus op veiligheid en betrouwbare ritregistratie.' },
        {
          value: 'van_camper',
          label: 'Bestelwagen / Camper',
          description: 'Voor langere voertuigen en specifieke raammontage.',
        },
        {
          value: 'truck_bus',
          label: 'Vrachtwagen / Bus',
          description: 'Voor grote voertuigen waar zicht en kabelbereik zwaarder meewegen.',
        },
      ],
    },
    {
      key: 'window_shape',
      shortLabel: 'Raam',
      summaryLabel: 'Raam',
      kicker: 'Vraag 2',
      question: 'Heeft je voertuig een recht of schuin raam?',
      helper: 'Deze extra check tonen we alleen voor campers, bestelwagens, vrachtwagens en bussen.',
      condition: (answers) => isLargeVehicle(answers.vehicle_type),
      options: [
        { value: 'recht', label: 'Recht', description: 'Verticale of bijna verticale voorruit.' },
        { value: 'schuin', label: 'Schuin', description: 'Meer zoals een normale, schuin geplaatste voorruit.' },
      ],
    },
    {
      key: 'mount_type',
      shortLabel: 'Bevestiging',
      summaryLabel: 'Bevestiging',
      kicker: 'Vraag 3',
      question: 'Hoe wil je de dashcam bevestigen?',
      helper: 'De montagevorm helpt ons om meteen de meest praktische match te selecteren.',
      condition: (answers) => !shouldSkipMountAndCamera(answers),
      options: [
        {
          value: 'adhesive',
          label: 'Zelfklevend',
          description: 'Strak en stevig met 3M-tape — de dashcam schuif je zo van de mount af, en met de elektrostatische sticker wisselt het nog makkelijker.',
        },
        {
          value: 'suction',
          label: 'Zuignap',
          description: 'Makkelijk te plaatsen en handig bij verticale ruiten.',
        },
        {
          value: 'magnetic',
          label: 'Magnetisch',
          description: 'Snel los te halen en extra compact in gebruik.',
        },
      ],
    },
    {
      key: 'camera_count',
      shortLabel: "Camera's",
      summaryLabel: "Camera's",
      kicker: 'Vraag 4',
      question: "Hoeveel camera's wil je?",
      helper: 'Meer camera\'s geven bredere dekking, maar niet iedereen heeft dat nodig.',
      condition: (answers) => !shouldSkipMountAndCamera(answers),
      options: [
        { value: '1', label: '1 camera', description: 'Compacte frontcamera zonder extra camera\'s.' },
        { value: '2', label: "2 camera's", description: 'Voor- en achterdekking tijdens iedere rit.' },
        { value: '3', label: "3 camera's", description: 'Voor, achter en interieur in één setup.' },
      ],
    },
    {
      key: 'parking_mode',
      shortLabel: 'Parkeerstand',
      summaryLabel: 'Parkeerbewaking',
      kicker: 'Vraag 5',
      question: 'Wil je parkeerbewaking?',
      helper: 'Zo weten we of bescherming buiten het rijden ook belangrijk is.',
      options: [
        { value: 'yes', label: 'Ja, graag', description: 'Ook beschermd als het voertuig geparkeerd staat.' },
        { value: 'no', label: 'Nee, niet nodig', description: 'De focus ligt vooral op registreren tijdens het rijden.' },
      ],
    },
    {
      key: 'budget',
      shortLabel: 'Prijs vs beeld',
      summaryLabel: 'Prijs vs beeld',
      kicker: 'Vraag 6',
      question: 'Wat weegt voor jou zwaarder?',
      helper: 'Deze laatste stap bepaalt de nadruk van het advies.',
      options: [
        { value: 'price', label: 'Scherpste prijs', description: 'Beste prijs-kwaliteit, zonder onnodige extra\'s.' },
        { value: 'image', label: 'Premium beeld', description: 'De scherpste opnames met de SONY-sensor.' },
      ],
    },
  ];

  // ── Deterministische beslis-hierarchie (eerste passende regel wint) ──
  // 1. Groot voertuig + recht raam -> D1 Pro
  // 2. 3 camera's -> M800 Pro
  // 3. Magnetische bevestiging -> T8 Pro (+ achtercamera bij 2 camera's)
  // 4. 2 camera's -> zuignap = D1 Pro, zelfklevend: premium = T8 Pro (+achtercamera), prijs = X30 Pro
  // 5. 1 camera -> premium beeld = T8 Pro, scherpste prijs: zuignap = D1 Pro, anders X30 Pro
  const decide = (answers) => {
    const large = isLargeVehicle(answers.vehicle_type);
    if (large && answers.window_shape === 'recht') return { key: 'd1_pro', rearCamera: false };
    if (answers.camera_count === '3') return { key: 'm800_pro', rearCamera: false };
    if (answers.mount_type === 'magnetic') {
      return { key: 't8_pro', rearCamera: answers.camera_count === '2' };
    }
    if (answers.camera_count === '2') {
      if (answers.mount_type === 'suction') return { key: 'd1_pro', rearCamera: false };
      // Zelfklevend: premium beeld -> T8 Pro + achtercamera, scherpste prijs -> X30 Pro
      if (answers.budget === 'image') return { key: 't8_pro', rearCamera: true };
      return { key: 'x30_pro', rearCamera: false };
    }
    // 1 camera
    if (answers.budget === 'price') {
      // Zuignap gekozen -> D1 Pro (heeft zuignap en scherpere prijs dan T8), anders X30 Pro
      if (answers.mount_type === 'suction') return { key: 'd1_pro', rearCamera: false };
      return { key: 'x30_pro', rearCamera: false };
    }
    return { key: 't8_pro', rearCamera: false };
  };

  const PROFILE_LOGIC = {
    t8_pro: {
      title: 'Camigoo T8 Pro Dashcam',
      intro: 'Sterke match: een compacte frontcamera die je snel losklikt en weer vastzet.',
      introByBudget: {
        image: 'Sterke match als je een compacte premium frontcamera wilt met haarscherp beeld.',
        price: 'Sterke match: een compacte frontcamera met magnetische mount die je zo losklikt en weer vastzet.',
      },
      badge: 'Beste match',
      reasons: {
        vehicle: {
          auto: 'Voor een auto houdt dit model het compact en eenvoudig, zonder onnodige extra\'s.',
          taxi: 'In een taxi een compacte, discrete frontcamera.',
          van_camper: 'Een compacte frontcamera, ook bruikbaar bij grotere voertuigen.',
          truck_bus: 'Een compacte frontcamera wanneer je bewust voor één sterke lens kiest.',
        },
        window: {
          schuin: 'Bij een schuin raam blijft de compacte montage strak en discreet in beeld.',
        },
        mount: {
          magnetic: 'Je voorkeur voor magnetische bevestiging past precies bij de T8 Pro — het enige model met magnetische mount.',
        },
        camera: {
          '1': 'Je wilt 1 camera, en daar is de T8 Pro het strakst op afgestemd.',
          '2': "Je wilt 2 camera's: de T8 Pro vul je aan met de losse achtercamera.",
        },
        budget: {
          image: 'Je koos voor premium beeld — de SONY Starvis-sensor en 4K leveren de scherpste opnames, ook in het donker.',
          price: 'Een compacte frontcamera die je in seconden losklikt en weer vastzet.',
        },
        parking: {
          yes: 'Parkeerbewaking is mogelijk in combinatie met de Hardwire Kit Pro.',
        },
      },
      base: 'WiFi, app-bediening en nachtzicht houden deze keuze compleet en overzichtelijk.',
      parkingNote: '24 uur parkeerbewaking werkt met de Camigoo Hardwire Kit Pro.',
    },
    d1_pro: {
      title: 'Camigoo D1 Pro',
      intro: 'De logische keuze voor een 2-camera setup die ook sterk werkt bij grotere voertuigen en verticale ruiten.',
      badge: 'Beste match',
      reasons: {
        vehicle: {
          auto: 'Ook voor een auto blijft dit een sterke allround 2-camera keuze.',
          taxi: 'Voor taxiwerk een logische voor- en achteroplossing.',
          van_camper: 'Sluit goed aan bij bestelwagens en campers dankzij de geschiktheid voor grotere voertuigen.',
          truck_bus: 'De meest logische route voor vrachtwagens en bussen zodra verticale ruiten meewegen.',
        },
        window: {
          recht: 'Bij een recht of verticaal raam is de D1 Pro de enige passende match in de selectie.',
          schuin: 'Ook bij een schuiner raam blijft dit een praktische 2-camera keuze.',
        },
        mount: {
          suction: 'Je voorkeur voor een zuignap sluit precies op de D1 Pro aan.',
        },
        camera: {
          '2': "Je zoekt een 2-camera setup, en daar is de D1 Pro direct op ingericht.",
        },
        budget: {
          price: 'Sterke voor- en achterdekking met een scherpe prijs.',
          image: '4K voor en Full HD achter in een sterk totaalpakket.',
        },
        parking: {
          yes: 'Parkeerbewaking blijft beschikbaar voor bescherming bij stilstand.',
        },
      },
      base: 'De achtercamera met 8 meter kabel maakt dit model extra bruikbaar bij grotere voertuigen.',
      parkingNote:
        '24 uur parkeerbewaking vereist de Camigoo Hardwire Kit Pro. Let op: bij dit model vervalt de GPS-functie in combinatie met die hardwire kit.',
    },
    x30_pro: {
      title: 'Camigoo X30 Pro',
      intro: 'De slimme keuze als je 2 camera\'s wilt met ingebouwde GPS en veel opslag, of de voordeligste route zoekt.',
      badge: 'Beste match',
      reasons: {
        vehicle: {
          auto: 'Voor een auto een krachtige 2-camera keuze met ingebouwde GPS.',
          taxi: 'Voor taxi\'s sterk dankzij GPS en betrouwbare ritvastlegging.',
          van_camper: 'Werkt goed voor grotere voertuigen zonder verticale-ruitmontage.',
          truck_bus: 'Interessant wanneer GPS en opslag zwaarder wegen dan de raamvorm.',
        },
        window: {
          schuin: 'Bij een schuin raam past deze vaste montage goed.',
        },
        mount: {
          adhesive: 'Je voorkeur voor een vaste zelfklevende montage past bij de X30 Pro.',
        },
        camera: {
          '2': "Je voorkeur voor 2 camera's matcht met een sterke voor- en achteropstelling.",
        },
        budget: {
          price: 'De voordeligste route, mét ingebouwde GPS en tot 512 GB opslag.',
          image: '4K voor en Full HD achter — sterk op beeld én prijs.',
        },
        parking: {
          yes: 'Parkeerbewaking sluit sterk aan, met behoud van de ingebouwde GPS.',
        },
      },
      base: 'Ingebouwde GPS en ondersteuning tot 512 GB maken dit model sterk voor langere ritten.',
      parkingNote: '24 uur parkeerbewaking werkt met de Camigoo Hardwire Kit Pro.',
    },
    m800_pro: {
      title: 'Camigoo M800 Pro',
      intro: 'De meest complete keuze met drie camera\'s, een interieurcamera en maximale dekking. Door ANWB verkozen tot beste 3-camera dashcam van Nederland in 2025.',
      badge: 'Beste match',
      reasons: {
        vehicle: {
          auto: 'Ook in een auto de juiste keuze wanneer je maximale registratie rondom de rit wilt.',
          taxi: 'Voor taxi\'s sterk dankzij brede dekking én zicht in het interieur.',
          van_camper: 'Aantrekkelijk voor grotere voertuigen die verder gaan dan alleen voor en achter.',
          truck_bus: 'Sterk wanneer volledige dekking belangrijker is dan een simpele montage.',
        },
        window: {
          schuin: 'Bij een schuin raam passen de vaste montage en brede dekking goed.',
        },
        mount: {
          adhesive: 'De vaste 3M-montage sluit aan op je voorkeur voor zelfklevende bevestiging.',
        },
        camera: {
          '3': "Je wilt 3 camera's, en de M800 Pro is het enige model dat daarop is gebouwd.",
        },
        budget: {
          image: 'Met 4K voor, 2K achter en een Full HD binnencamera krijg je het meest complete beeldpakket.',
          price: 'Maximale dekking; dit model is minder gericht op de laagste prijs.',
        },
        parking: {
          yes: 'Parkeerbewaking past goed bij maximale registratie, ook stilstaand.',
        },
      },
      base: 'Voor-, achter- en binnencamera geven de meest complete opname rondom.',
      parkingNote: '24 uur parkeerbewaking werkt in combinatie met de Camigoo Hardwire Kit Pro.',
    },
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const uniq = (items) => {
    const seen = new Set();
    return items.filter((item) => item && !seen.has(item) && seen.add(item));
  };

  const parsePayload = (section) => {
    const node = section.querySelector('[data-quiz-payload]');
    if (!node) return {};

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error('SA Dashcam Quiz payload error', error);
      return {};
    }
  };

  class SADashcamQuiz {
    constructor(section) {
      this.section = section;
      this.payload = parsePayload(section);
      this.productsByProfile = {};
      this.accessories = {};
      this.currentRecommendation = null;
      this.state = { currentStepKey: QUIZ_STEPS[0].key, answers: {}, resultVisible: false };
      this.handleSectionClick = this.handleSectionClick.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.elements = {
        progressLabel: section.querySelector('[data-quiz-step-label]'),
        progressPercent: section.querySelector('[data-quiz-progress-percent]'),
        progressBar: section.querySelector('[data-quiz-progress-bar]'),
        steps: section.querySelector('[data-quiz-steps]'),
        introActions: section.querySelector('[data-quiz-intro-actions]'),
        introPrevButton: section.querySelector('[data-quiz-intro-prev]'),
        introRestartButton: section.querySelector('[data-quiz-intro-restart]'),
        result: section.querySelector('[data-quiz-result]'),
        resultTitle: section.querySelector('[data-result-title]'),
        resultIntro: section.querySelector('[data-result-intro]'),
        resultBadge: section.querySelector('[data-result-badge]'),
        resultPrice: section.querySelector('[data-result-price]'),
        resultReasons: section.querySelector('[data-result-reasons]'),
        resultNote: section.querySelector('[data-result-note]'),
        resultImage: section.querySelector('[data-result-image]'),
        resultSticker: section.querySelector('[data-result-sticker]'),
        resultPlaceholder: section.querySelector('[data-result-placeholder]'),
        viewButton: section.querySelector('[data-result-view]'),
        addButton: section.querySelector('[data-result-add]'),
        addButtonLabel: section.querySelector('[data-result-add-label]'),
        addons: section.querySelector('[data-quiz-addons]'),
        footer: section.querySelector('[data-quiz-footer]'),
        prevButton: section.querySelector('[data-quiz-prev]'),
        nextButton: section.querySelector('[data-quiz-next]'),
        restartButton: section.querySelector('[data-quiz-restart]'),
        liveRegion: section.querySelector('[data-quiz-live]'),
      };

      this.buildProductMap();
      this.renderStaticMarkup();
      this.section.addEventListener('click', this.handleSectionClick);
      window.addEventListener('pageshow', this.handlePageShow);

      // Luister mee met winkelwagen-wijzigingen die niet door onszelf komen
      // (bijv. iemand die een add-on of de dashcam uit de side cart gooit),
      // zodat "Toegevoegd"-knoppen weer klikbaar worden zodra het product
      // écht niet meer in de winkelwagen zit.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          this.handleExternalCartUpdate(event);
        });
      }

      this.syncUI();
    }

    // Kom je terug op deze pagina via de bfcache (bijv. terug-knop na het mandje
    // te hebben leeggehaald), dan kan de UI nog "Toegevoegd" tonen terwijl het
    // product intussen weer verwijderd is. Check dan de echte winkelwagen-inhoud.
    handlePageShow(event) {
      if (event.persisted) {
        if (this.elements.addButton) this.elements.addButton.classList.remove('is-loading');
        this.handleExternalCartUpdate({ source: 'bfcache' });
      }
    }

    // Wordt aangeroepen bij elke winkelwagen-wijziging in de pagina (via het
    // theme-brede pub/sub-kanaal). Onze eigen toevoegingen negeren we (die
    // zetten hun knop al meteen goed) — voor de rest checken we de actuele
    // winkelwagen en zetten we knoppen van verwijderde producten terug.
    async handleExternalCartUpdate(event) {
      if (event && event.source === 'sa-dashcam-quiz') return;

      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
        if (!response.ok) return;
        const cart = await response.json();
        const idsInCart = new Set((cart.items || []).map((item) => item.variant_id));
        this.resetStaleAddedButtons(idsInCart);
      } catch (error) {}
    }

    // Houdt de "Toegevoegd"-status van de knoppen synchroon met de echte
    // winkelwagen-inhoud, in beide richtingen: knoppen die "Toegevoegd" tonen
    // terwijl het product er niet meer in zit -> terug naar "Toevoegen". En
    // knoppen die "Toevoegen" tonen terwijl het product er (weer) in zit
    // — bijv. opnieuw toegevoegd via de upsell-knop in de side cart —
    // -> weer naar "Toegevoegd".
    resetStaleAddedButtons(idsInCart) {
      this.section.querySelectorAll('.sa-dashcam-quiz__addon-btn[data-variant-id]').forEach((button) => {
        const variantId = Number(button.dataset.variantId);
        if (!variantId) return;

        const labelEl = button.querySelector('.sa-dashcam-quiz__addon-btn-label');
        const isMarkedAdded = button.classList.contains('is-added');
        const isInCart = idsInCart.has(variantId);

        if (isMarkedAdded && !isInCart) {
          button.classList.remove('is-added', 'is-loading');
          button.disabled = false;
          if (labelEl) labelEl.textContent = 'Toevoegen';
          return;
        }

        // Alleen knoppen die zelf ooit direct-toevoegen konden weer op
        // "Toegevoegd" zetten; "Niet beschikbaar" of "Bekijk"-knoppen (die
        // niet rechtstreeks toevoegen) laten we met rust.
        if (!isMarkedAdded && isInCart && button.dataset.canDirectAdd === 'true') {
          button.classList.remove('is-loading');
          button.classList.add('is-added');
          button.disabled = true;
          if (labelEl) labelEl.textContent = 'Toegevoegd';
        }
      });

      if (this.elements.addButton && this.elements.addButtonLabel) {
        const mainVariantId = Number(this.elements.addButton.dataset.variantId);
        const isInCart = mainVariantId ? idsInCart.has(mainVariantId) : false;
        const currentLabel = this.elements.addButtonLabel.textContent;

        if (currentLabel === 'Toegevoegd' && !isInCart) {
          this.elements.addButton.classList.remove('is-loading');
          this.elements.addButton.disabled = false;
          this.elements.addButtonLabel.textContent = this.payload.addLabel || 'Direct toevoegen';
        } else if (
          currentLabel !== 'Toegevoegd' &&
          isInCart &&
          this.elements.addButton.dataset.canDirectAdd === 'true'
        ) {
          this.elements.addButton.classList.remove('is-loading');
          this.elements.addButton.disabled = true;
          this.elements.addButtonLabel.textContent = 'Toegevoegd';
        }
      }
    }

    buildProductMap() {
      const blocks = Array.isArray(this.payload.blocks) ? this.payload.blocks : [];
      this.productsByProfile = {};

      blocks.forEach((block) => {
        if (block?.profileKey) this.productsByProfile[block.profileKey] = block;
      });

      this.accessories = this.payload.accessories || {};
    }

    renderStaticMarkup() {
      if (this.elements.steps) {
        this.elements.steps.innerHTML = QUIZ_STEPS.map((step) => {
          const optionsMarkup = step.options
            .map(
              (option, optionIndex) => `
                <button
                  type="button"
                  class="sa-dashcam-quiz__option"
                  data-quiz-option
                  data-step-key="${escapeHtml(step.key)}"
                  data-step-value="${escapeHtml(option.value)}"
                  aria-pressed="false"
                >
                  <span class="sa-dashcam-quiz__option-index">${optionIndex + 1}</span>
                  <span class="sa-dashcam-quiz__option-body">
                    <span class="sa-dashcam-quiz__option-title">${escapeHtml(option.label)}</span>
                    <span class="sa-dashcam-quiz__option-text">${escapeHtml(option.description)}</span>
                  </span>
                  <span class="sa-dashcam-quiz__option-indicator" aria-hidden="true"></span>
                </button>
              `
            )
            .join('');

          return `
            <section class="sa-dashcam-quiz__step" data-step-panel="${escapeHtml(step.key)}" hidden>
              <div class="sa-dashcam-quiz__step-head">
                <span class="sa-dashcam-quiz__step-kicker">${escapeHtml(step.kicker)}</span>
                <h3 class="sa-dashcam-quiz__step-title">${escapeHtml(step.question)}</h3>
                <p class="sa-dashcam-quiz__step-help">${escapeHtml(step.helper)}</p>
              </div>
              <div class="sa-dashcam-quiz__options">${optionsMarkup}</div>
            </section>
          `;
        }).join('');
      }

      this.stepPanels = Array.from(this.section.querySelectorAll('[data-step-panel]'));
      this.optionButtons = Array.from(this.section.querySelectorAll('[data-quiz-option]'));
    }

    getVisibleSteps(answers = this.state.answers) {
      return QUIZ_STEPS.filter((step) => (typeof step.condition === 'function' ? step.condition(answers) : true));
    }

    pruneAnswers() {
      const visibleKeys = new Set(this.getVisibleSteps().map((step) => step.key));

      Object.keys(this.state.answers).forEach((key) => {
        if (!visibleKeys.has(key)) delete this.state.answers[key];
      });
    }

    getCurrentStepIndex(visibleSteps = this.getVisibleSteps()) {
      return visibleSteps.findIndex((step) => step.key === this.state.currentStepKey);
    }

    ensureCurrentStep(visibleSteps = this.getVisibleSteps()) {
      if (!visibleSteps.length) return null;

      const currentIndex = this.getCurrentStepIndex(visibleSteps);
      if (currentIndex !== -1) return visibleSteps[currentIndex];

      const nextUnanswered = visibleSteps.find((step) => !this.state.answers[step.key]);
      const fallbackStep = nextUnanswered || visibleSteps[visibleSteps.length - 1];

      this.state.currentStepKey = fallbackStep.key;
      return fallbackStep;
    }

    handleSectionClick(event) {
      const optionButton = event.target.closest('[data-quiz-option]');
      if (optionButton && this.section.contains(optionButton)) {
        this.selectAnswer(optionButton.dataset.stepKey, optionButton.dataset.stepValue);
        this.flashAndAdvance(optionButton, optionButton.dataset.stepKey);
        return;
      }

      const addonButton = event.target.closest('[data-quiz-addon-add]');
      if (addonButton && this.section.contains(addonButton)) {
        return this.handleAddonAdd(addonButton);
      }

      if (event.target.closest('[data-quiz-next]')) return this.goNext();
      if (event.target.closest('[data-quiz-prev]')) return this.goPrev();
      if (event.target.closest('[data-quiz-intro-prev]')) return this.goPrev();
      if (event.target.closest('[data-quiz-restart]')) return this.restart();
      if (event.target.closest('[data-quiz-intro-restart]')) return this.restart();
      if (event.target.closest('[data-result-add]')) return this.handleAddToCart();
    }

    selectAnswer(stepKey, value) {
      if (!stepKey || !value) return;

      this.state.answers[stepKey] = value;
      this.state.resultVisible = false;
      this.pruneAnswers();
      this.syncUI();
    }

    flashAndAdvance(button, stepKey) {
      if (button) {
        button.classList.remove('is-flashing');
        // Forceer een reflow zodat de animatie opnieuw start, ook bij dezelfde keuze.
        void button.offsetWidth;
        button.classList.add('is-flashing');
      }

      window.clearTimeout(this.advanceTimer);
      this.advanceTimer = window.setTimeout(() => {
        if (button) button.classList.remove('is-flashing');

        const visibleSteps = this.getVisibleSteps();
        const currentStep = this.ensureCurrentStep(visibleSteps);
        if (!this.state.resultVisible && currentStep && currentStep.key === stepKey) {
          this.goNext();
        }
      }, 480);
    }

    goNext() {
      if (this.state.resultVisible) return;

      const visibleSteps = this.getVisibleSteps();
      const currentStep = this.ensureCurrentStep(visibleSteps);
      const currentIndex = this.getCurrentStepIndex(visibleSteps);

      if (!currentStep || currentIndex === -1 || !this.state.answers[currentStep.key]) return;

      if (currentIndex === visibleSteps.length - 1) {
        this.state.resultVisible = true;
      } else {
        this.state.currentStepKey = visibleSteps[currentIndex + 1].key;
      }

      this.syncUI();
    }

    goPrev() {
      const visibleSteps = this.getVisibleSteps();

      if (this.state.resultVisible) {
        this.state.resultVisible = false;
        this.state.currentStepKey = visibleSteps[visibleSteps.length - 1]?.key || QUIZ_STEPS[0].key;
      } else {
        const currentIndex = this.getCurrentStepIndex(visibleSteps);
        if (currentIndex > 0) this.state.currentStepKey = visibleSteps[currentIndex - 1].key;
      }

      this.syncUI();

      // Bij "Vorige" vanuit het adviesscherm (na de add-ups) sta je vaak diep
      // gescrold. Zonder dit blijft de pagina op die scrollpositie staan en
      // zie je een willekeurig stuk van de sectie in plaats van de vorige
      // vraag zelf. Scroll daarom naar de vraag toe.
      this.scrollPanelIntoView();
    }

    // Scrollt naar het begin van het vraag-/adviesscherm (met ruimte voor een
    // eventuele sticky header), zodat je na "Vorige" altijd meteen de vraag
    // zelf ziet in plaats van waar de pagina toevallig stond.
    scrollPanelIntoView() {
      const target = this.section.querySelector('.sa-dashcam-quiz__panel') || this.section;
      if (!target) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let headerOffset = 16;
      const cssHeaderHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );
      if (!Number.isNaN(cssHeaderHeight) && cssHeaderHeight > 0) {
        headerOffset += cssHeaderHeight;
      }

      const top = Math.max(target.getBoundingClientRect().top + window.pageYOffset - headerOffset, 0);

      // Alleen scrollen als de vraag niet al (grotendeels) in beeld staat.
      if (Math.abs(window.pageYOffset - top) < 12) return;

      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
    }

    restart() {
      this.state = { currentStepKey: QUIZ_STEPS[0].key, answers: {}, resultVisible: false };
      this.currentRecommendation = null;
      this.completeTracked = false;
      this.syncUI();
      this.say('De keuzehulp is opnieuw gestart.');
    }

    trackComplete() {
      if (this.completeTracked) return;
      this.completeTracked = true;
      var model = (this.currentRecommendation && (this.currentRecommendation.name || this.currentRecommendation.title)) || 'keuzehulp';
      try {
        if (typeof window.fbq === 'function') {
          window.fbq('trackCustom', 'KeuzehulpVoltooid', { content_name: model });
        }
      } catch (e) {}
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'keuzehulp_voltooid', model: model });
      } catch (e) {}
      try {
        document.dispatchEvent(new CustomEvent('camigoo:keuzehulp-voltooid', { detail: { model: model } }));
      } catch (e) {}
    }

    syncUI() {
      this.pruneAnswers();
      const visibleSteps = this.getVisibleSteps();
      this.ensureCurrentStep(visibleSteps);

      if (this.elements.steps) {
        this.elements.steps.hidden = this.state.resultVisible;
      }

      this.updateProgress(visibleSteps);
      this.updateSteps();
      this.updateFooter(visibleSteps);

      if (this.state.resultVisible) {
        this.renderResult();
        this.trackComplete();
      } else if (this.elements.result) {
        this.elements.result.hidden = true;
      }
    }

    updateProgress(visibleSteps = this.getVisibleSteps()) {
      const total = visibleSteps.length;
      let percent;
      if (this.state.resultVisible) {
        percent = 100;
      } else {
        // Volg de positie van de huidige vraag, zodat de balk meebeweegt bij vooruit én terug.
        const currentIndex = this.getCurrentStepIndex(visibleSteps);
        const position = currentIndex < 0 ? 0 : currentIndex;
        percent = total ? Math.round((position / total) * 100) : 0;
      }

      if (this.elements.progressLabel) this.elements.progressLabel.textContent = 'Voortgang';
      if (this.elements.progressPercent) this.elements.progressPercent.textContent = `${percent}%`;
      if (this.elements.progressBar) this.elements.progressBar.style.width = `${percent}%`;
    }

    updateSteps() {
      const visibleSteps = this.getVisibleSteps();
      const currentStep = this.ensureCurrentStep(visibleSteps);

      const stepNumberByKey = {};
      visibleSteps.forEach((step, index) => {
        stepNumberByKey[step.key] = index + 1;
      });

      this.stepPanels.forEach((panel) => {
        const isActive = !this.state.resultVisible && currentStep && panel.dataset.stepPanel === currentStep.key;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);

        const number = stepNumberByKey[panel.dataset.stepPanel];
        if (number) {
          const kicker = panel.querySelector('.sa-dashcam-quiz__step-kicker');
          if (kicker) kicker.textContent = `Vraag ${number}`;
        }
      });

      this.optionButtons.forEach((button) => {
        const isSelected = this.state.answers[button.dataset.stepKey] === button.dataset.stepValue;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });
    }

    updateFooter(visibleSteps = this.getVisibleSteps()) {
      const currentIndex = this.getCurrentStepIndex(visibleSteps);
      const restartLabel = this.payload.restartLabel || 'Opnieuw beginnen';

      if (this.elements.footer) {
        this.elements.footer.hidden = false;
        this.elements.footer.classList.toggle('is-result', this.state.resultVisible);
      }

      if (this.elements.introActions) {
        this.elements.introActions.hidden = !this.state.resultVisible;
      }

      if (this.elements.prevButton) {
        this.elements.prevButton.hidden = false;
        this.elements.prevButton.disabled = this.state.resultVisible ? false : currentIndex <= 0;
      }

      if (this.elements.nextButton) {
        // Auto-doorgaan na een antwoord; de losse "Volgende"-knop is niet meer nodig.
        this.elements.nextButton.hidden = true;
      }

      if (this.elements.restartButton) {
        this.elements.restartButton.hidden = !this.state.resultVisible;
        this.elements.restartButton.textContent = restartLabel;
      }

      if (this.elements.introPrevButton) {
        this.elements.introPrevButton.disabled = false;
      }

      if (this.elements.introRestartButton) {
        this.elements.introRestartButton.textContent = restartLabel;
      }
    }

    getRecommendation() {
      const designMode = Boolean(this.payload.designMode);
      const { key, rearCamera } = decide(this.state.answers);
      const logic = PROFILE_LOGIC[key];
      if (!logic) return null;

      const entry = this.productsByProfile[key];
      if (!entry && !designMode) {
        return { entry: { profileKey: key, isConfigured: false, product: {} }, logic, needsRearCamera: false };
      }

      return {
        entry: entry || { profileKey: key, isConfigured: false, product: {} },
        logic,
        needsRearCamera: Boolean(rearCamera),
      };
    }

    buildReasons(logic, needsRearCamera) {
      const answers = this.state.answers;
      const cameraReason = needsRearCamera ? logic.reasons.camera?.['2'] : logic.reasons.camera?.[answers.camera_count];
      return uniq([
        logic.reasons.vehicle?.[answers.vehicle_type],
        logic.reasons.window?.[answers.window_shape],
        logic.reasons.mount?.[answers.mount_type],
        cameraReason,
        logic.reasons.budget?.[answers.budget],
        logic.reasons.parking?.[answers.parking_mode],
        logic.base,
      ]).slice(0, 3);
    }

    buildNote(entry, logic) {
      const notes = [];
      if (this.state.answers.parking_mode === 'yes' && logic.parkingNote) notes.push(logic.parkingNote);
      if (entry.customResultNote) notes.push(entry.customResultNote);
      return uniq(notes).join(' ');
    }

    getAddonGroups(recommendation) {
      const answers = this.state.answers;
      const acc = this.accessories;
      const headings = this.payload.addonHeadings || {};
      const profileKey = recommendation.entry.profileKey;
      const groups = [];

      // Achtercamera — alleen bij T8 Pro via de 2-camera route
      if (recommendation.needsRearCamera && acc.rearCamera && acc.rearCamera.isConfigured) {
        groups.push({
          heading: headings.rearcam || 'Maak je setup compleet',
          items: [{ ...acc.rearCamera, reason: 'Full HD achtercamera voor zicht naar achteren' }],
        });
      }

      // Hardwire kit — bij parkeerbewaking (staat boven de zuignap)
      if (answers.parking_mode === 'yes' && acc.hardwire && acc.hardwire.isConfigured) {
        groups.push({
          heading: headings.hardwire || 'Voor 24/7 parkeerbewaking',
          items: [{ ...acc.hardwire, reason: 'Sluit je dashcam aan op de auto-accu' }],
        });
      }

      // Zuignap — alleen bij D1 Pro: toon beide zuignappen als keuze
      if (profileKey === 'd1_pro') {
        const items = [];
        if (acc.suctionStraight && acc.suctionStraight.isConfigured) {
          items.push({ ...acc.suctionStraight, reason: 'Speciaal voor verticale of rechte voorruiten' });
        }
        if (acc.suctionExtra && acc.suctionExtra.isConfigured) {
          items.push({ ...acc.suctionExtra, reason: 'Handig om de dashcam in een tweede voertuig te gebruiken' });
        }
        if (items.length) {
          groups.push({
            heading: headings.suction || 'Extra zuignap nodig?',
            items,
          });
        }
      }

      // Elektrostatische sticker — bij elk model met plak- of magneetmontage
      // (T8 Pro, X30 Pro, M800 Pro). De D1 Pro gebruikt een zuignap en krijgt deze niet.
      if (profileKey !== 'd1_pro' && acc.sticker && acc.sticker.isConfigured) {
        groups.push({
          heading: headings.sticker || 'Eenvoudig afneembaar maken',
          items: [{ ...acc.sticker, reason: 'Lijmvrij en herbruikbaar, zonder plakresten' }],
        });
      }

      // Magnetische mount — alleen bij T8 Pro, altijd als laatste (minst belangrijk)
      if (profileKey === 't8_pro' && acc.t8Mount && acc.t8Mount.isConfigured) {
        groups.push({
          heading: headings.t8mount || 'Extra magnetische mount?',
          items: [{ ...acc.t8Mount, reason: 'Voor in een tweede voertuig of gewoon een extra mount' }],
        });
      }

      return groups;
    }

    renderAddonCard(addon) {
      const canDirectAdd = Boolean(addon.canDirectAdd && addon.variantId && addon.available);
      const priceMarkup = addon.priceText
        ? `<span class="sa-dashcam-quiz__addon-price">${escapeHtml(addon.priceText)}</span>`
        : '';
      const media = addon.image
        ? `<img src="${escapeHtml(addon.image)}" alt="${escapeHtml(addon.imageAlt || addon.title)}" loading="lazy" width="120" height="120">`
        : `<span class="sa-dashcam-quiz__addon-media-fallback" aria-hidden="true"></span>`;
      const btnLabel = !addon.available ? 'Niet beschikbaar' : canDirectAdd ? 'Toevoegen' : 'Bekijk';

      return `
        <div class="sa-dashcam-quiz__addon">
          <div class="sa-dashcam-quiz__addon-media">${media}</div>
          <div class="sa-dashcam-quiz__addon-info">
            <span class="sa-dashcam-quiz__addon-name">${escapeHtml(addon.title)}</span>
            <span class="sa-dashcam-quiz__addon-reason">${escapeHtml(addon.reason)}</span>
          </div>
          <div class="sa-dashcam-quiz__addon-side">
            ${priceMarkup}
            <button
              type="button"
              class="sa-dashcam-quiz__addon-btn"
              data-quiz-addon-add
              data-variant-id="${escapeHtml(addon.variantId || '')}"
              data-product-url="${escapeHtml(addon.url || '')}"
              data-can-direct-add="${canDirectAdd ? 'true' : 'false'}"
              ${!addon.available ? 'disabled' : ''}
            >
              <span class="sa-dashcam-quiz__addon-btn-label">${escapeHtml(btnLabel)}</span>
            </button>
          </div>
        </div>
      `;
    }

    renderAddonGroups(groups) {
      if (!this.elements.addons) return;

      if (!groups.length) {
        this.elements.addons.hidden = true;
        this.elements.addons.innerHTML = '';
        return;
      }

      this.elements.addons.hidden = false;
      this.elements.addons.innerHTML = groups
        .map(
          (group) => `
            <div class="sa-dashcam-quiz__addon-group">
              <p class="sa-dashcam-quiz__addons-heading">${escapeHtml(group.heading)}</p>
              <div class="sa-dashcam-quiz__addons-list">
                ${group.items.map((item) => this.renderAddonCard(item)).join('')}
              </div>
            </div>
          `
        )
        .join('');
    }

    renderResult() {
      if (!this.elements.result) return;

      const recommendation = this.getRecommendation();
      this.currentRecommendation = recommendation;
      this.elements.result.hidden = false;

      if (!recommendation) {
        return this.renderEmpty(
          this.payload.designMode
            ? 'Voeg productblokken toe om de aanbeveling te kunnen previewen.'
            : 'Er zijn nog geen gekoppelde dashcam-profielen beschikbaar in deze keuzehulp.'
        );
      }

      const { entry, logic, needsRearCamera } = recommendation;
      const product = entry.product || {};
      const title = product.title || logic.title;
      const intro =
        entry.customResultIntro ||
        (logic.introByBudget && logic.introByBudget[this.state.answers.budget]) ||
        logic.intro;
      const badge = entry.resultBadge || logic.badge;
      const reasons = this.buildReasons(logic, needsRearCamera);
      const note = this.buildNote(entry, logic);
      const isConfigured = Boolean(entry.isConfigured);
      const canDirectAdd = Boolean(product.canDirectAdd && product.variantId && product.available);
      const addLabel = !isConfigured
        ? 'Koppel product'
        : !product.available
          ? 'Niet beschikbaar'
          : canDirectAdd
            ? (this.payload.addLabel || 'Direct toevoegen')
            : 'Kies uitvoering';

      if (this.elements.resultTitle) this.elements.resultTitle.textContent = title;
      if (this.elements.resultIntro) this.elements.resultIntro.textContent = intro;

      if (this.elements.resultBadge) {
        this.elements.resultBadge.hidden = !badge;
        this.elements.resultBadge.textContent = badge || '';
      }

      if (this.elements.resultPrice) {
        if (product.priceText) {
          const compareMarkup = product.compareAtPriceText
            ? `<span class="sa-dashcam-quiz__result-price-compare">${escapeHtml(product.compareAtPriceText)}</span>`
            : '';
          this.elements.resultPrice.hidden = false;
          this.elements.resultPrice.innerHTML = `
            <span class="sa-dashcam-quiz__result-price-current">${escapeHtml(product.priceText)}</span>
            ${compareMarkup}
          `;
        } else {
          this.elements.resultPrice.hidden = true;
          this.elements.resultPrice.innerHTML = '';
        }
      }

      if (this.elements.resultReasons) {
        this.elements.resultReasons.innerHTML = reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('');
      }

      if (this.elements.resultNote) {
        this.elements.resultNote.hidden = !note;
        this.elements.resultNote.textContent = note || '';
      }

      if (this.elements.resultSticker) {
        this.elements.resultSticker.hidden = !(product.hasAnwbBadge && product.image);
      }

      if (this.elements.resultImage && this.elements.resultPlaceholder) {
        if (product.image) {
          this.elements.resultImage.hidden = false;
          this.elements.resultImage.src = product.image;
          this.elements.resultImage.alt = product.imageAlt || title;
          this.elements.resultPlaceholder.hidden = true;
          this.elements.resultPlaceholder.textContent = '';
        } else {
          this.elements.resultImage.hidden = true;
          this.elements.resultImage.removeAttribute('src');
          this.elements.resultImage.alt = '';
          this.elements.resultPlaceholder.hidden = false;
          this.elements.resultPlaceholder.textContent = isConfigured ? title : 'Koppel product in editor';
        }
      }

      if (this.elements.viewButton) {
        this.elements.viewButton.textContent = this.payload.viewLabel || 'Bekijk product';
        this.elements.viewButton.classList.toggle('is-disabled', !isConfigured);
        this.elements.viewButton.setAttribute('aria-disabled', !isConfigured ? 'true' : 'false');
        if (isConfigured && product.url) {
          this.elements.viewButton.href = product.url;
        } else {
          this.elements.viewButton.removeAttribute('href');
        }
      }

      if (this.elements.addButton && this.elements.addButtonLabel) {
        this.elements.addButton.disabled = !isConfigured || !product.available;
        this.elements.addButton.dataset.variantId = product.variantId || '';
        this.elements.addButton.dataset.productUrl = product.url || '';
        this.elements.addButton.dataset.canDirectAdd = canDirectAdd ? 'true' : 'false';
        this.elements.addButtonLabel.textContent = addLabel;
      }

      this.renderAddonGroups(this.getAddonGroups(recommendation));

      this.say(`Advies klaar. Beste match: ${title}.`);
    }

    renderEmpty(message) {
      if (this.elements.resultTitle) this.elements.resultTitle.textContent = 'Keuzehulp nog niet compleet';
      if (this.elements.resultIntro) this.elements.resultIntro.textContent = message;
      if (this.elements.resultBadge) {
        this.elements.resultBadge.hidden = true;
        this.elements.resultBadge.textContent = '';
      }
      if (this.elements.resultPrice) {
        this.elements.resultPrice.hidden = true;
        this.elements.resultPrice.innerHTML = '';
      }
      if (this.elements.resultReasons) this.elements.resultReasons.innerHTML = '';
      if (this.elements.resultNote) {
        this.elements.resultNote.hidden = true;
        this.elements.resultNote.textContent = '';
      }
      if (this.elements.resultImage && this.elements.resultPlaceholder) {
        this.elements.resultImage.hidden = true;
        this.elements.resultImage.removeAttribute('src');
        this.elements.resultImage.alt = '';
        this.elements.resultPlaceholder.hidden = false;
        this.elements.resultPlaceholder.textContent = 'Editor setup nodig';
      }
      if (this.elements.resultSticker) {
        this.elements.resultSticker.hidden = true;
      }
      if (this.elements.addons) {
        this.elements.addons.hidden = true;
        this.elements.addons.innerHTML = '';
      }
      if (this.elements.viewButton) {
        this.elements.viewButton.classList.add('is-disabled');
        this.elements.viewButton.setAttribute('aria-disabled', 'true');
        this.elements.viewButton.removeAttribute('href');
      }
      if (this.elements.addButton && this.elements.addButtonLabel) {
        this.elements.addButton.disabled = true;
        this.elements.addButton.dataset.variantId = '';
        this.elements.addButton.dataset.productUrl = '';
        this.elements.addButton.dataset.canDirectAdd = 'false';
        this.elements.addButtonLabel.textContent = 'Niet beschikbaar';
      }
    }

    // Werkt alleen het winkelwagen-icoontje (aantal) in de header bij, zonder de
    // side cart te openen. Zo blijft de gebruiker in het keuzehulp-eindscherm en
    // kan die rustig meerdere add-ons achter elkaar toevoegen.
    refreshCartIconBubble(data) {
      try {
        const sectionHtml = data && data.sections && data.sections['cart-icon-bubble'];
        if (!sectionHtml) return;
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (parsed && target) target.innerHTML = parsed.innerHTML;
      } catch (error) {}
    }

    // Ververst de inhoud van de side cart in stilte (zonder 'm te openen), zodat
    // die niet stale blijft. Zonder dit bleef de drawer de OUDE inhoud tonen
    // (van vóór dit toevoegen) zodra je 'm later zelf via het winkelwagen-icoon
    // opende — het product zát wel echt in de winkelwagen, maar was niet te zien.
    refreshCartDrawerContentsQuietly(cartUI, data) {
      try {
        const sectionHtml = data && data.sections && data.sections['cart-drawer'];
        if (!sectionHtml) return;

        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const src = parsed.querySelector('#CartDrawer');
        const dest = document.querySelector('#CartDrawer');
        const srcHtml = src ? (src.innerHTML || '') : '';

        if (src && dest && srcHtml.replace(/\s+/g, '').length > 30) {
          dest.innerHTML = srcHtml;
          cartUI.classList.remove('is-empty');
          const innerEl = cartUI.querySelector('.drawer__inner');
          if (innerEl) innerEl.classList.remove('is-empty');
        }
      } catch (error) {}
    }

    // `openDrawer: false` update het mandje wel (voorraad/aantal), maar laat de
    // side cart dicht. Gebruikt binnen het keuzehulp-eindscherm zodat de
    // gebruiker niet steeds onderbroken wordt terwijl die nog add-ons wil kiezen.
    async addToCart(variantId, { openDrawer = true } = {}) {
      const cartUI = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = { id: variantId, quantity: 1 };

      if (cartUI && typeof cartUI.getSectionsToRender === 'function') {
        payload.sections = cartUI.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(this.payload.cartAddUrl || window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sa-dashcam-quiz',
          productVariantId: variantId,
          cartData: data,
        });
      }

      if (cartUI && cartUI.classList.contains('is-empty')) {
        cartUI.classList.remove('is-empty');
      }

      if (cartUI && data.sections) {
        this.refreshCartIconBubble(data);

        if (openDrawer && typeof cartUI.renderContents === 'function') {
          cartUI.renderContents(data);
        } else if (!openDrawer) {
          this.refreshCartDrawerContentsQuietly(cartUI, data);
        }

        return { data, refreshed: true };
      }

      return { data, refreshed: false };
    }

    async handleAddToCart() {
      if (!this.currentRecommendation || !this.elements.addButton || !this.elements.addButtonLabel) return;

      const button = this.elements.addButton;
      const canDirectAdd = button.dataset.canDirectAdd === 'true';
      const variantId = button.dataset.variantId;
      const productUrl = button.dataset.productUrl;

      if (!canDirectAdd) {
        if (productUrl) window.location.href = productUrl;
        return;
      }

      if (!variantId || button.disabled) return;

      const originalLabel = this.payload.addLabel || 'Direct toevoegen';
      button.disabled = true;
      button.classList.add('is-loading');
      this.elements.addButtonLabel.textContent = 'Bezig...';

      try {
        const result = await this.addToCart(variantId, { openDrawer: false });
        if (!result.refreshed) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }
        this.elements.addButtonLabel.textContent = 'Toegevoegd';
        this.say('Product toegevoegd aan je winkelwagen.');
      } catch (error) {
        console.error('SA Dashcam Quiz add-to-cart error', error);
        this.elements.addButtonLabel.textContent = 'Probeer opnieuw';
        this.say('Toevoegen aan winkelwagen is niet gelukt.');
      } finally {
        window.setTimeout(() => {
          button.classList.remove('is-loading');
          button.disabled = false;
          this.elements.addButtonLabel.textContent = originalLabel;
        }, 1400);
      }
    }

    async handleAddonAdd(button) {
      const labelEl = button.querySelector('.sa-dashcam-quiz__addon-btn-label');
      const canDirectAdd = button.dataset.canDirectAdd === 'true';
      const variantId = button.dataset.variantId;
      const productUrl = button.dataset.productUrl;

      if (!canDirectAdd) {
        if (productUrl) window.location.href = productUrl;
        return;
      }

      if (!variantId || button.disabled) return;

      const originalLabel = labelEl ? labelEl.textContent : 'Toevoegen';
      button.disabled = true;
      button.classList.add('is-loading');
      if (labelEl) labelEl.textContent = 'Bezig...';

      try {
        const result = await this.addToCart(variantId, { openDrawer: false });
        if (!result.refreshed) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }
        if (labelEl) labelEl.textContent = 'Toegevoegd';
        button.classList.add('is-added');
        this.say('Accessoire toegevoegd aan je winkelwagen.');
      } catch (error) {
        console.error('SA Dashcam Quiz add-on error', error);
        if (labelEl) labelEl.textContent = 'Probeer opnieuw';
        this.say('Toevoegen van het accessoire is niet gelukt.');
        window.setTimeout(() => {
          button.classList.remove('is-loading');
          button.disabled = false;
          if (labelEl) labelEl.textContent = originalLabel;
        }, 1400);
      }
    }

    say(message) {
      if (!this.elements.liveRegion) return;
      this.elements.liveRegion.textContent = '';
      window.setTimeout(() => {
        this.elements.liveRegion.textContent = message;
      }, 10);
    }

    destroy() {
      this.section.removeEventListener('click', this.handleSectionClick);
      window.removeEventListener('pageshow', this.handlePageShow);
      if (typeof this.unsubscribeCartUpdate === 'function') this.unsubscribeCartUpdate();
    }
  }

  const instances = new WeakMap();
  const initSection = (section) => {
    if (!section || instances.has(section)) return;
    instances.set(section, new SADashcamQuiz(section));
  };
  const destroySection = (section) => {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy?.();
    instances.delete(section);
  };
  const initAll = (root = document) => root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!event?.target) return;
    if (event.target.matches?.(SECTION_SELECTOR)) return initSection(event.target);
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event?.target) return;
    if (event.target.matches?.(SECTION_SELECTOR)) return destroySection(event.target);
    event.target.querySelectorAll?.(SECTION_SELECTOR).forEach(destroySection);
  });
})();